import type { TravelInfo, TripDefinition } from "../types.js";

export type LatLng = [number, number];

/** Haversine distance in km (R = 6371). */
export function hav(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLa = ((b[0] - a[0]) * Math.PI) / 180;
  const dLo = ((b[1] - a[1]) * Math.PI) / 180;
  const q =
    Math.sin(dLa / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLo / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(q));
}

/**
 * Distance → travel estimate, verbatim from the reference:
 *  - missing geo on either end → flat 10 min
 *  - < 0.12 km → flat 2 min, no icon
 *  - ≤ 1.7 km → walk @4.5 km/h, floor 3 min, rounded to 5 → 🚶
 *  - else drive @35 km/h + 10 min overhead, rounded to 5, capped 240 → 🚗 (🚤 if > 30 km)
 */
export function travelMin(a: LatLng | null, b: LatLng | null): TravelInfo {
  if (!a || !b) return { min: 10, icon: "" };
  const d = hav(a, b);
  if (d < 0.12) return { min: 2, icon: "" };
  if (d <= 1.7) return { min: Math.max(3, Math.round(((d / 4.5) * 60) / 5) * 5), icon: "🚶" };
  const m = Math.round(((d / 35) * 60 + 10) / 5) * 5;
  return { min: Math.min(m, 240), icon: d > 30 ? "🚤" : "🚗" };
}

export function geoOf(def: TripDefinition, actId: string): LatLng | null {
  const g = def.geo[actId];
  return g ? [g[0], g[1]] : null;
}

/**
 * Travel to an activity from the previous stop. Fixed boat/train overrides win:
 * outbound by destination (boat[actId]), return legs by origin (boat[prevId]).
 */
export function travelTo(
  def: TripDefinition,
  actId: string,
  prevGeo: LatLng | null,
  prevId: string | null
): TravelInfo {
  if (def.boat[actId]) return def.boat[actId];
  if (prevId && def.boat[prevId]) return def.boat[prevId];
  return travelMin(prevGeo, geoOf(def, actId));
}
